import { FormattedMessage, useIntl } from 'react-intl'
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons'

import { OrderSummaryTextField } from 'Modules/order/components/OrderSummary/components/OrderSummaryTextField'
import {
  getExternalIdCustomName,
  getOrderAttribute,
} from 'Modules/order/utils/order.utils'

import { Alert, ButtonGroup, CardBody, Heading } from 'Components/_theme'
import { ModalDevicePresentationBlock } from 'Components/ModalDevicePresentationBlock/ModalDevicePresentationBlock'
import { ModalErrorState } from 'Components/ModalErrorState/ModalErrorState'

import { countryISOCodes } from 'Constants/constants'

import { useStore } from 'Portal/src/stores'

import type { ModalSummaryProps } from './ModalSummary.types'

export const ModalSummary = (props: ModalSummaryProps) => {
  const {
    formId,
    isEverphoneReplacement,
    isLoading,
    rental,
    shippingFormData,
    leftButtonE2ESelector,
    rightButtonE2ESelector,
    submitError,
    showAlert,
    onSubmit,
    onPreviousStep,
    heading,
    type,
    orderExternalId,
    onSetExternalId,
  } = props
  const intl = useIntl()

  const { organisationStore } = useStore()
  const { organisationConfigList } = organisationStore

  const hasError = !!submitError
  const isUnitedStatesAddressType =
    shippingFormData?.country?.value === countryISOCodes.US

  const organisationConfigAttributeForThisOrder = getOrderAttribute(
    organisationConfigList,
    type as 'lostStolen' | 'replaceBroken',
  )

  const isExternalIdEnabledForThisType =
    organisationConfigAttributeForThisOrder &&
    organisationConfigAttributeForThisOrder.attributeValue !== 'disabled'

  const isExternalIdFieldMandatory =
    organisationConfigAttributeForThisOrder?.attributeValue === 'mandatory'

  const externalIdCustomName = getExternalIdCustomName(
    organisationConfigList,
    intl,
  )

  return (
    <>
      {heading}

      <CardBody>
        {hasError ? (
          <ModalErrorState error={submitError} />
        ) : (
          <>
            {showAlert && (
              <Alert iconLeft={faCircleInfo} variant="info">
                <FormattedMessage id="LostOrStolenModal_followInternalProcesses" />
              </Alert>
            )}

            <ModalDevicePresentationBlock className="mt-6" rental={rental}>
              {shippingFormData && (
                <>
                  <h6>
                    <FormattedMessage id="Shipping_address" />
                  </h6>

                  <p>
                    {shippingFormData.name}
                    <br />
                    {shippingFormData.nameAddition}
                    <br />
                    {isUnitedStatesAddressType
                      ? shippingFormData.usAddress
                      : `${shippingFormData.street} / ${shippingFormData.streetNumber}`}
                    <br />
                    {shippingFormData.zipcode} /{' '}
                    {isUnitedStatesAddressType
                      ? `${shippingFormData.city}, ${shippingFormData.state}`
                      : shippingFormData.city}
                    <br />
                    {shippingFormData.country?.label}
                  </p>
                </>
              )}
            </ModalDevicePresentationBlock>

            {isEverphoneReplacement && (
              <p className="small">
                <FormattedMessage id="ExchangeDeviceModal_shipping_label_info" />
              </p>
            )}

            {isExternalIdEnabledForThisType && (
              <>
                <Heading as={6} className="mt-6" size={5}>
                  {externalIdCustomName}
                </Heading>
                <OrderSummaryTextField
                  controlId="orderExternalId"
                  defaultValue={orderExternalId || ''}
                  isRequired={isExternalIdFieldMandatory}
                  onChangeLabel={event => onSetExternalId(event.target.value)}
                  placeholderTranslationId={
                    isExternalIdFieldMandatory
                      ? 'NewOrderSummary_external_id_placeholder'
                      : 'NewOrderSummary_external_id_placeholder_optional'
                  }
                  placeholderTranslationValues={{
                    customName: externalIdCustomName,
                  }}
                />
              </>
            )}
          </>
        )}

        <ButtonGroup
          leftButton={{
            'data-e2e': leftButtonE2ESelector,
            onClick: onPreviousStep,
            text: intl.formatMessage({ id: 'Back' }),
          }}
          rightButton={
            hasError
              ? undefined
              : {
                  'data-e2e': rightButtonE2ESelector,
                  disabled:
                    isLoading ||
                    (isExternalIdFieldMandatory && !orderExternalId),
                  form: formId,
                  onClick: onSubmit,
                  text: intl.formatMessage({ id: 'Submit' }),
                  type: 'submit',
                }
          }
        />
      </CardBody>
    </>
  )
}
